import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';
import { getLaboklinApi } from '../../services/laboklinApi';
import type { LabReportSummary } from '../../types/laboklin';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';

/**
 * The MyLab reports table.
 *
 * Columns match the live portal. The only addition is the "Ask GekkoVet"
 * action, kept visibly separate so it is obvious how little of the existing
 * screen the integration changes.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

interface ReportsListViewProps {
  onAskAboutReport: (reportNo: string) => void;
}

export const ReportsListView: React.FC<ReportsListViewProps> = ({ onAskAboutReport }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<LabReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getLaboklinApi()
      .listReports()
      .then((data) => {
        if (!cancelled) setReports(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">Could not load reports from Laboklin: {error}</Alert>;
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="outlined" color="primary" onClick={() => navigate('/mylab/order/new')}>
          New online order
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report no.</TableCell>
              <TableCell>Report date</TableCell>
              <TableCell>Receive date</TableCell>
              <TableCell>Pet owner</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Species</TableCell>
              <TableCell>Breed</TableCell>
              <TableCell>Report type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.reportNo} hover>
                <TableCell>
                  <Link onClick={() => navigate(`/mylab/reports/${r.reportNo}`)}>{r.reportNo}</Link>
                </TableCell>
                <TableCell>{formatDate(r.reportDate)}</TableCell>
                <TableCell>{formatDate(r.receiveDate)}</TableCell>
                <TableCell>{r.petOwner ?? ''}</TableCell>
                <TableCell>{r.petName ?? ''}</TableCell>
                <TableCell>{r.species ?? ''}</TableCell>
                <TableCell>{r.breed ?? ''}</TableCell>
                <TableCell sx={{ color: '#2e7d32' }}>{r.reportType ?? ''}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <OpenInNewIcon sx={{ fontSize: 18, color: LABOKLIN_RED }} />
                    <PictureAsPdfIcon sx={{ fontSize: 18, color: LABOKLIN_RED }} />
                    <ShareIcon sx={{ fontSize: 18, color: LABOKLIN_RED }} />
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => onAskAboutReport(r.reportNo)}
                      sx={{ ml: 1, whiteSpace: 'nowrap' }}
                    >
                      Ask GekkoVet
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {reports.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', p: 3, textAlign: 'center' }}>
          No reports available on this account.
        </Typography>
      )}
    </Paper>
  );
};

export default ReportsListView;
